import { useCallback } from 'react';
import type { UseFormSetValue } from 'react-hook-form';
import type { DealParam, DealUpdateParam } from '~/models/DealForm';
import type { PropertyBuildingOption } from '~/models/PropertyBuildingOption';
import { buildBuildingOption } from '~/models/PropertyBuildingOption';
import type { PropertyRoomOption } from '~/models/PropertyRoomOption';
import { buildRoomOption } from '~/models/PropertyRoomOption';
import {
  buildPropertyByPropertyRoomOption,
  buildPropertyByPropertyBuildingOption
} from '~/models/kensaku_kun/property';

type DealFormParam = DealParam | DealUpdateParam;
type DealFormValues = DealFormParam[keyof DealFormParam];
type Props = {
  setValue: UseFormSetValue<DealFormParam>;
  propertyBuildingOptions: PropertyBuildingOption[];
  propertyRoomOptions: PropertyRoomOption[];
  selectedBuilding: PropertyBuildingOption | null;
  selectedRoom: PropertyRoomOption | null;
  setSelectedBuilding: (property: PropertyBuildingOption | null) => void;
  setSelectedRoom: (property: PropertyRoomOption | null) => void;
};
type ReturnType = {
  onChangeBuilding: (option: { value: string } | null) => void;
  onChangeRoom: (option: { value: string } | null) => void;
  onChangeRegisterAsBuildingCheck: (checked: boolean) => void;
  onBlurBuilding: () => void;
  onBlurRoom: () => void;
};

const buildResetBuildingValues = (): [
  key: keyof DealFormParam,
  value: DealFormValues
][] => [
  ['property', null],
  ['roomName', ''],
  ['roomNumber', ''],
  ['address', ''],
  ['isRegisterAsBuilding', false],
  ['amaterasBuildingPropertyId', null]
];
const buildBuildingValues = (
  building: PropertyBuildingOption
): [key: keyof DealFormParam, value: DealFormValues][] => [
  ['roomName', building.name],
  ['roomNumber', ''],
  ['address', building.address],
  ['isRegisterAsBuilding', false],
  [
    'amaterasBuildingPropertyId',
    building.amaterasPropertyId ? String(building.amaterasPropertyId) : null
  ],
  ['placeKeyStoring', building.placeKeyStoring ?? ''],
  ['freeParking', building.freeParking ?? ''],
  ['enableElevator', building.enableElevator],
  ['enableAutoLock', building.enableAutoLock]
];
const buildResetRoomValues = (): [keyof DealFormParam, DealFormValues][] => [
  ['property', null],
  ['roomNumber', '']
];
const buildRoomValues = (
  room: PropertyRoomOption
): [key: keyof DealFormParam, value: DealFormValues][] => [
  ['property', buildPropertyByPropertyRoomOption(room)],
  ['roomNumber', room.roomNumber]
];
const setValues = (
  setValue: UseFormSetValue<DealParam | DealUpdateParam>,
  values: [key: keyof DealFormParam, value: DealFormValues][]
): void => {
  values.forEach(([key, value]) => setValue(key, value));
};

export const usePropertySelectHandler = ({
  setValue,
  propertyBuildingOptions,
  propertyRoomOptions,
  selectedBuilding,
  selectedRoom,
  setSelectedBuilding,
  setSelectedRoom
}: Props): ReturnType => {
  const resetBuildingFields = useCallback((): void => {
    setSelectedBuilding(null);
    setSelectedRoom(null);
    setValues(setValue, buildResetBuildingValues());
  }, [setSelectedBuilding, setSelectedRoom, setValue]);
  const setBuildingFields = useCallback(
    (selectingBuilding: PropertyBuildingOption): void => {
      setSelectedBuilding(selectingBuilding);
      setSelectedRoom(null);
      setValues(setValue, buildBuildingValues(selectingBuilding));
    },
    [setSelectedBuilding, setSelectedRoom, setValue]
  );
  const resetRoomFields = useCallback((): void => {
    setSelectedRoom(null);
    setValues(setValue, buildResetRoomValues());
  }, [setSelectedRoom, setValue]);
  const setRoomFields = useCallback(
    (selectingRoom: PropertyRoomOption): void => {
      setSelectedRoom(selectingRoom);
      setValues(setValue, buildRoomValues(selectingRoom));
    },
    [setSelectedRoom, setValue]
  );

  // 選択肢を選択したときに使うhandler
  const onChangeBuilding = useCallback(
    (option: { value: string } | null) => {
      const value = option?.value ?? '';
      if (!value) {
        resetBuildingFields();
        return;
      }
      const selecting = propertyBuildingOptions.find(
        (building) => building.key === value
      );
      if (selecting) setBuildingFields(selecting);
    },
    [propertyBuildingOptions, resetBuildingFields, setBuildingFields]
  );

  const onChangeRoom = useCallback(
    (option: { value: string } | null) => {
      const value = option?.value ?? '';
      if (!value) {
        resetRoomFields();
        return;
      }
      const selecting = propertyRoomOptions.find((room) => room.key === value);
      if (selecting) setRoomFields(selecting);
    },
    [propertyRoomOptions, resetRoomFields, setRoomFields]
  );

  // 棟として登録のチェックボックスを選択したときに使うhandler
  const onChangeRegisterAsBuildingCheck = useCallback(
    (checked: boolean) => {
      setSelectedRoom(null);
      const baseValues: [keyof DealFormParam, DealFormValues][] = [
        ['property', buildPropertyByPropertyBuildingOption(selectedBuilding)],
        ['roomNumber', ''],
        ['isRegisterAsBuilding', checked]
      ];
      const values = checked
        ? baseValues.concat([['amaterasBuildingPropertyId', null]])
        : baseValues;
      setValues(setValue, values);
    },
    [setValue, setSelectedRoom, selectedBuilding]
  );

  // 選択肢を選択せずにフォーカスが外れたときは手動入力した値を選択肢として登録するhandler
  const onBlurBuilding = useCallback((): void => {
    if (selectedBuilding) return;
    const firstOption = propertyBuildingOptions[0];
    if (firstOption?.key === 'manual-input-building') {
      const option = buildBuildingOption(firstOption);
      onChangeBuilding(option);
    }
  }, [propertyBuildingOptions, onChangeBuilding, selectedBuilding]);

  const onBlurRoom = useCallback((): void => {
    if (selectedRoom) return;
    const firstOption = propertyRoomOptions[0];
    if (firstOption?.key === 'manual-input-room') {
      const option = buildRoomOption(firstOption);
      onChangeRoom(option);
    }
  }, [propertyRoomOptions, onChangeRoom, selectedRoom]);

  return {
    onChangeBuilding,
    onChangeRoom,
    onChangeRegisterAsBuildingCheck,
    onBlurBuilding,
    onBlurRoom
  };
};
